// src/app/settings/accruals/pages/employee-leave-balances/employee-leave-balances.component.ts

import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { Subject, forkJoin } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  finalize,
  takeUntil,
} from 'rxjs/operators';

import { AccrualsService } from '../accruals.service';
import {
  EmployeeBalanceSummaryRow,
  EmployeeUser,
  LeavePolicy,
  LeaveType,
  UserFilterValues,
} from '../models/accruals.model';
import { AccrualsNavComponent } from '../../../shared/accruals-nav/accruals-nav.component';

interface EmployeeBalanceFiltersVm {
  searchTerm: string;
  companyId: number | null;
  departmentId: number | null;
}

interface ManualAdjustmentFormModel {
  leaveType: LeaveType | null;
  adjustmentMode: 'add' | 'subtract';
  hours: number | null;
  note: string;
}

interface AssignPolicyFormModel {
  leaveType: LeaveType | null;
  policyId: number | null;
  startDate: string;
}

@Component({
  selector: 'app-employee-leave-balances',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, AccrualsNavComponent],
  templateUrl: './employee-leave-balances.component.html',
  styleUrls: ['./employee-leave-balances.component.scss'],
})
export class EmployeeLeaveBalancesComponent implements OnInit, OnDestroy {
  isLoading = false;
  errorMessage: string | null = null;

  allEmployees: EmployeeUser[] = [];
  filteredEmployees: EmployeeBalanceSummaryRow[] = [];
  summaryRows: EmployeeBalanceSummaryRow[] = [];

  userFilterValues: UserFilterValues | null = null;
  allPolicies: LeavePolicy[] = [];

  currentPage = 1;
  totalPages = 1;
  pageSize = 25;
  pageSizes = [25, 50, 100, 250];

  filters: EmployeeBalanceFiltersVm = {
    searchTerm: '',
    companyId: null,
    departmentId: null,
  };

  searchInputValue = '';
  private readonly searchInput$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();

  manualAdjustmentModalOpen = false;
  isSavingManualAdjustment = false;
  selectedEmployeeForAdjustment: EmployeeBalanceSummaryRow | null = null;
  manualAdjustmentForm: ManualAdjustmentFormModel = this.getEmptyManualAdjustmentForm();

  assignPolicyModalOpen = false;
  isAssigningPolicy = false;
  selectedEmployeeForPolicyAssignment: EmployeeBalanceSummaryRow | null = null;
  assignPolicyForm: AssignPolicyFormModel = this.getEmptyAssignPolicyForm();

  toastVisible = false;
  toastMessage = '';
  toastType: 'success' | 'error' = 'success';

  constructor(
    private readonly accrualsService: AccrualsService,
    private readonly cd: ChangeDetectorRef,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    this.bindSearch();
    this.loadInitialData();
  }

  ngOnDestroy(): void {
    this.unlockPageScroll();
    this.searchInput$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  get hasActiveFilters(): boolean {
    return !!(
      this.filters.searchTerm.trim() ||
      this.filters.companyId !== null ||
      this.filters.departmentId !== null
    );
  }

  get pagedEmployees(): EmployeeBalanceSummaryRow[] {
    const startIndex = (this.currentPage - 1) * this.pageSize;
    return this.filteredEmployees.slice(startIndex, startIndex + this.pageSize);
  }

  get assignablePolicies(): LeavePolicy[] {
    if (!this.assignPolicyForm.leaveType) {
      return [];
    }

    return this.allPolicies
      .filter((policy) => policy.leaveType === this.assignPolicyForm.leaveType)
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  loadInitialData(): void {
    this.isLoading = true;
    this.errorMessage = null;
    this.cd.detectChanges();

    forkJoin({
      users: this.accrualsService.getAllActiveUsers(),
      userFilterValues: this.accrualsService.getUserFilterValues(),
      policies: this.accrualsService.getLeavePolicies(),
    })
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.cd.detectChanges();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: ({ users, userFilterValues, policies }) => {
          this.allEmployees = (users ?? []).filter(
            (user) => user.employeeStatus === 'Active' && !user.isDeleted,
          );

          this.userFilterValues = userFilterValues
            ? {
                ...userFilterValues,
                departments: [...(userFilterValues.departments ?? [])].sort(
                  (a, b) => Number(a.code) - Number(b.code),
                ),
              }
            : null;

          this.allPolicies = policies ?? [];
          this.summaryRows = this.buildSummaryRows(this.allEmployees);
          this.filteredEmployees = [...this.summaryRows];
          this.currentPage = 1;
          this.updatePagination();

          this.cd.detectChanges();
        },
        error: () => {
          this.errorMessage = 'Failed to load employee leave balances.';
          this.allEmployees = [];
          this.summaryRows = [];
          this.filteredEmployees = [];
          this.currentPage = 1;
          this.totalPages = 1;
          this.cd.detectChanges();
        },
      });
  }

  private bindSearch(): void {
    this.searchInput$
      .pipe(debounceTime(1000), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((value) => {
        this.filters.searchTerm = value;
        this.currentPage = 1;
        this.applyFilters();
      });
  }

  onSearchTermChange(value: string | null): void {
    const normalized = String(value ?? '').trimStart();
    this.searchInputValue = normalized;
    this.searchInput$.next(normalized);
  }

  onCompanyChange(value: number | string | null): void {
    this.filters.companyId = this.toNullableNumber(value);
    this.currentPage = 1;
    this.applyFilters();
  }

  onDepartmentChange(value: number | string | null): void {
    this.filters.departmentId = this.toNullableNumber(value);
    this.currentPage = 1;
    this.applyFilters();
  }

  clearFilters(): void {
    this.filters = {
      searchTerm: '',
      companyId: null,
      departmentId: null,
    };

    this.searchInputValue = '';
    this.currentPage = 1;
    this.applyFilters();
  }

  applyFilters(): void {
    const search = this.filters.searchTerm.trim().toLowerCase();

    this.filteredEmployees = this.summaryRows.filter((row) => {
      const employee = this.allEmployees.find((user) => user.id === row.employeeId);

      const matchesSearch =
        !search ||
        row.fullName.toLowerCase().includes(search) ||
        row.employeeNumber.toLowerCase().includes(search) ||
        row.companyName.toLowerCase().includes(search) ||
        row.departmentName.toLowerCase().includes(search);

      const matchesCompany =
        this.filters.companyId === null ||
        employee?.company?.id === this.filters.companyId;

      const matchesDepartment =
        this.filters.departmentId === null ||
        employee?.department?.id === this.filters.departmentId;

      return matchesSearch && matchesCompany && matchesDepartment;
    });

    this.updatePagination();
    this.cd.detectChanges();
  }

  changePage(page: number): void {
    if (page < 1 || page > this.totalPages) {
      return;
    }

    this.currentPage = page;
    this.cd.detectChanges();
  }

  changePageSize(size: number): void {
    this.pageSize = Number(size);
    this.currentPage = 1;
    this.updatePagination();
    this.cd.detectChanges();
  }

  goToEmployeeDetail(row: EmployeeBalanceSummaryRow): void {
    this.router.navigate(['/settings/accruals/employee-leave-balances', row.employeeId]);
  }

  openManualAdjustmentModal(row: EmployeeBalanceSummaryRow): void {
    this.selectedEmployeeForAdjustment = row;
    this.manualAdjustmentForm = this.getEmptyManualAdjustmentForm();
    this.manualAdjustmentModalOpen = true;
    this.lockPageScroll();
    this.cd.detectChanges();
  }

  closeManualAdjustmentModal(): void {
    this.manualAdjustmentModalOpen = false;
    this.selectedEmployeeForAdjustment = null;
    this.manualAdjustmentForm = this.getEmptyManualAdjustmentForm();
    this.unlockPageScroll();
    this.cd.detectChanges();
  }

  saveManualAdjustment(): void {
    if (!this.selectedEmployeeForAdjustment) {
      return;
    }

    if (
      !this.manualAdjustmentForm.leaveType ||
      !this.manualAdjustmentForm.hours ||
      this.manualAdjustmentForm.hours <= 0 ||
      !this.manualAdjustmentForm.note.trim()
    ) {
      this.showToast('Complete all manual adjustment fields before saving', 'error');
      return;
    }

    const normalizedHours =
      this.manualAdjustmentForm.adjustmentMode === 'subtract'
        ? -Math.abs(this.manualAdjustmentForm.hours)
        : Math.abs(this.manualAdjustmentForm.hours);

    this.isSavingManualAdjustment = true;
    this.cd.detectChanges();

    this.accrualsService
      .createManualAdjustment({
        employeeId: this.selectedEmployeeForAdjustment.employeeId,
        leaveType: this.manualAdjustmentForm.leaveType,
        hours: normalizedHours,
        note: this.manualAdjustmentForm.note.trim(),
        timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York',
      })
      .pipe(
        finalize(() => {
          this.isSavingManualAdjustment = false;
          this.cd.detectChanges();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((success) => {
        if (!success) {
          this.showToast('Failed to save manual adjustment', 'error');
          return;
        }

        this.closeManualAdjustmentModal();
        this.showToast('Manual adjustment saved successfully', 'success');
        this.loadInitialData();
      });
  }

  openAssignPolicyModal(row: EmployeeBalanceSummaryRow): void {
    this.selectedEmployeeForPolicyAssignment = row;
    this.assignPolicyForm = this.getEmptyAssignPolicyForm();
    this.assignPolicyModalOpen = true;
    this.lockPageScroll();
    this.cd.detectChanges();
  }

  closeAssignPolicyModal(): void {
    this.assignPolicyModalOpen = false;
    this.selectedEmployeeForPolicyAssignment = null;
    this.assignPolicyForm = this.getEmptyAssignPolicyForm();
    this.unlockPageScroll();
    this.cd.detectChanges();
  }

  onAssignLeaveTypeChange(value: LeaveType | null): void {
    this.assignPolicyForm.leaveType = value;
    this.assignPolicyForm.policyId = null;
  }

  saveAssignedPolicy(): void {
    if (!this.selectedEmployeeForPolicyAssignment) {
      return;
    }

    if (
      !this.assignPolicyForm.leaveType ||
      !this.assignPolicyForm.policyId ||
      !this.assignPolicyForm.startDate
    ) {
      this.showToast('Complete all policy assignment fields before saving', 'error');
      return;
    }

    this.isAssigningPolicy = true;
    this.cd.detectChanges();

    this.accrualsService
      .assignEmployeesToPolicy(this.assignPolicyForm.policyId, {
        employeeIds: [this.selectedEmployeeForPolicyAssignment.employeeId],
        startDate: this.assignPolicyForm.startDate,
      })
      .pipe(
        finalize(() => {
          this.isAssigningPolicy = false;
          this.cd.detectChanges();
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((success) => {
        if (!success) {
          this.showToast('Failed to assign policy', 'error');
          return;
        }

        this.closeAssignPolicyModal();
        this.showToast('Policy assigned successfully', 'success');
        this.loadInitialData();
      });
  }

  getBalanceClass(value: number): string {
    if (value < 0) return 'negative';
    if (value > 0) return 'positive';
    return 'neutral';
  }

  trackByEmployeeId(_: number, item: EmployeeBalanceSummaryRow): number {
    return item.employeeId;
  }

  private updatePagination(): void {
    this.totalPages = Math.max(1, Math.ceil(this.filteredEmployees.length / this.pageSize));

    if (this.currentPage > this.totalPages) {
      this.currentPage = this.totalPages;
    }
  }

  private lockPageScroll(): void {
    document.body.classList.add('modal-open');
  }

  private unlockPageScroll(): void {
    if (!this.manualAdjustmentModalOpen && !this.assignPolicyModalOpen) {
      document.body.classList.remove('modal-open');
    }
  }

  private buildSummaryRows(users: EmployeeUser[]): EmployeeBalanceSummaryRow[] {
    return [...users]
      .map((user) => {
        const vacationBalance = user.employeeLeaveBalances.find((x) => x.leaveType === 'Vacation');
        const sickBalance = user.employeeLeaveBalances.find((x) => x.leaveType === 'Sick');

        return {
          employeeId: user.id,
          employeeNumber: user.employeeNumber,
          firstName: user.firstName,
          lastName: user.lastName,
          fullName: `${user.firstName} ${user.lastName}`,
          companyName: user.company?.name ?? '',
          departmentName: user.department?.name ?? '',
          departmentCode: String(user.department?.code ?? ''),
          vacationAvailableHours: vacationBalance?.availableHours ?? 0,
          vacationUsedHours: vacationBalance?.usedHours ?? 0,
          sickAvailableHours: sickBalance?.availableHours ?? 0,
          sickUsedHours: sickBalance?.usedHours ?? 0,
          vacationPolicyId: user.vacationLeavePolicy?.id ?? null,
          vacationPolicyName: user.vacationLeavePolicy?.leavePolicy?.name ?? '',
          sickPolicyName: user.sickLeavePolicy?.leavePolicy?.name ?? '',
          sickPolicyId: user.sickLeavePolicy?.id ?? null,
        };
      })
      .sort((a, b) => a.lastName.localeCompare(b.lastName) || a.firstName.localeCompare(b.firstName));
  }

  private getEmptyManualAdjustmentForm(): ManualAdjustmentFormModel {
    return {
      leaveType: null,
      adjustmentMode: 'add',
      hours: null,
      note: '',
    };
  }

  private getEmptyAssignPolicyForm(): AssignPolicyFormModel {
    return {
      leaveType: null,
      policyId: null,
      startDate: '',
    };
  }

  private toNullableNumber(value: number | string | null | undefined): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  private showToast(message: string, type: 'success' | 'error'): void {
    this.toastMessage = message;
    this.toastType = type;
    this.toastVisible = true;
    this.cd.detectChanges();

    setTimeout(() => {
      this.toastVisible = false;
      this.cd.markForCheck();
    }, 2500);
  }
}